import { Theme } from '@mui/material';

/**
 * Masse der gemeinsamen Bausteine, die ein Theme ueberschreiben kann
 * (Beratungsstrecke im Original-Look, Entscheid Alex 27.10.2026, Nachtrag
 * Decision #246).
 *
 * Das CRM-Theme traegt hier GENAU die Zahlen, die die Bausteine vorher selbst
 * im Code trugen — das CRM sieht damit unveraendert aus. Das Alt-Theme der
 * Strecke (`theme/streckeAlt/variante.ts`) setzt die Original-Werte dagegen.
 * Nur Masse, die Bausteine IN der Strecke wirklich tragen; kein Vorrat.
 *
 * `null` heisst: aus der Palette wie bisher.
 */
export interface BausteinMasse {
  /** Karte (`karteSx`): Ecke, Randfarbe, Innenabstand. */
  karte: { radius: string; rand: string; px: number; py: number };
  /** Kennzahlen-Leiste ueber den Fall-/Kundenkarten (`KundenUeberblick`). */
  ueberblick: { py: number; mb: number };
  /** Abstand zwischen den Kundenkarten (`CustomerBasicDataCards`). */
  kartenAbstand: number;
  /** Feld-Raster der Kundenkarten: Zeilen-/Spaltenabstand, Abstand nach oben. */
  feldRaster: {
    rowGap: number | string;
    columnGap: number | string;
    mt: number | null;
    /** Abstand nach oben im Personen-Raster (`PersonPaar`, eine Person). */
    mtPerson: number | null;
    spalte: string;
  };
  /** Mindesthoehe einer Feld-Beschriftung (zwei Zeilen, damit Werte buendig stehen). */
  labelMinHoehe: number;
  /** Abstand zwischen Kartenkopf und Inhalt einer aufklappbaren Karte. */
  aufklappAbstand: number;
  /** Abstand Icon–Text (Kartenkopf, Kennzahlen-Leiste). */
  iconAbstand: number;
  /** Kopf einer aufklappbaren Kundenkarte: Icon-Farbe, Pfeil (null = erbt / keine Deckkraft). */
  kartenkopf: { iconFarbe: string; pfeilFarbe: string | null; pfeilDeckkraft: number | null };
  /** Abschnitt innerhalb einer Kundenkarte (Linie oben): Abstand, Innenabstand, Linienfarbe. */
  trenner: { mt: number; pt: number; farbe: string };
  /** Abstand ueber einer Aktionszeile unter den Feldern („Partner hinzufuegen"). */
  aktionAbstand: number;
  /** Abstand zwischen Kinder-Liste und „Kind hinzufuegen" (`EditableChildrenCard`). */
  listenAbstand: number;
  /** Bearbeitbares Feld der Kundenkarten (`fieldStyles`). */
  feld: {
    hoehe: number;
    px: number;
    radius: string;
    rand: string;
    randHover: string;
    fokusBreite: string;
    labelPx: number;
    labelMb: number | null;
    wertFarbe: string;
    leerFarbe: string;
    hoverFlaeche: string;
  };
  /** Hausknopf `CTAButton`. */
  knopf: {
    radius: string;
    minHoehe: number;
    minHoeheKlein: number;
    randBreite: string;
    schrift: number;
    umrandetFlaeche: string | null;
    umrandetHover: string | null;
  };
  /** Eingabefelder `dsTextFieldSx`, `FilterSelect`, `SearchField`. */
  eingabe: { hoehe: number; radius: string; schrift: number; hinweis: number; wertFarbe: string };
}

/**
 * Masse aus dem Theme. Faellt auf die CRM-Werte zurueck, wo kein Haus-Theme
 * anliegt (Tests mit nacktem `createTheme()` oder ganz ohne Provider) — die
 * Bausteine sehen dort aus wie bisher.
 */
export const bm = (theme: Theme): BausteinMasse => theme.bausteinMasse ?? CRM_BAUSTEIN_MASSE;

export const CRM_BAUSTEIN_MASSE: BausteinMasse = {
  karte: { radius: '8px', rand: 'divider', px: 6, py: 6 },
  ueberblick: { py: 6, mb: 6 },
  kartenAbstand: 6,
  feldRaster: { rowGap: 4, columnGap: 6, mt: null, mtPerson: null, spalte: 'minmax(0, 1fr)' },
  labelMinHoehe: 32,
  aufklappAbstand: 4,
  iconAbstand: 2,
  kartenkopf: { iconFarbe: 'text.primary', pfeilFarbe: 'text.primary', pfeilDeckkraft: null },
  trenner: { mt: 4, pt: 4, farbe: 'divider' },
  aktionAbstand: 4,
  listenAbstand: 4,
  feld: {
    hoehe: 36,
    px: 2,
    radius: '8px',
    rand: 'divider',
    randHover: 'grey.200',
    fokusBreite: '1px',
    labelPx: 2,
    labelMb: null,
    wertFarbe: 'text.secondary',
    leerFarbe: 'text.muted',
    hoverFlaeche: 'background.card',
  },
  knopf: {
    radius: '8px',
    minHoehe: 36,
    minHoeheKlein: 32,
    randBreite: '1px',
    schrift: 14,
    umrandetFlaeche: null,
    umrandetHover: null,
  },
  eingabe: { hoehe: 36, radius: '8px', schrift: 14, hinweis: 12, wertFarbe: 'text.secondary' },
};
